import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with timeout and retry logic
const apiClient = axios.create({
  timeout: 30000, // 30 second timeout
});

// Silent token refresh: if the backend returns a fresh token in the response
// header (when the current one is within 30 days of expiry), swap it in
// localStorage so the user never gets bounced to login while active.
const installTokenRefreshInterceptor = () => {
  const handler = (response) => {
    const fresh = response?.headers?.['x-refreshed-token'];
    if (fresh) {
      const current = localStorage.getItem('vitala_token');
      if (fresh !== current) {
        localStorage.setItem('vitala_token', fresh);
        // Notify any listening AuthContext to update its state
        window.dispatchEvent(new CustomEvent('vitala:token-refreshed', { detail: fresh }));
      }
    }
    return response;
  };
  apiClient.interceptors.response.use(handler);
  axios.interceptors.response.use(handler);  // also catch direct axios calls
};
installTokenRefreshInterceptor();

// Retry logic for transient failures
const retryRequest = async (fn, retries = 2, delay = 1000) => {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (error) {
      const isLastAttempt = i === retries;
      const isRetryable = !error.response || error.response.status >= 500 || error.code === 'ECONNABORTED';
      
      if (isLastAttempt || !isRetryable) {
        throw error;
      }
      
      console.log(`Retrying request (attempt ${i + 2}/${retries + 1})...`);
      await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('vitala_token'));
  const [loading, setLoading] = useState(true);

  // Listen for silent-refresh events from the axios interceptor and sync React state
  useEffect(() => {
    const onRefresh = (e) => {
      const fresh = e?.detail;
      if (fresh && fresh !== token) {
        setToken(fresh);
      }
    };
    window.addEventListener('vitala:token-refreshed', onRefresh);
    return () => window.removeEventListener('vitala:token-refreshed', onRefresh);
  }, [token]);

  // Helper: detect browser timezone and silently sync to backend if needed
  const syncTimezone = async (authToken, currentTz) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!tz || tz === currentTz) return null;
      await apiClient.post(`${API}/auth/timezone`, { timezone: tz }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      console.log(`[Vitala] Timezone synced: ${tz}`);
      return tz;
    } catch (e) {
      console.warn('[Vitala] Timezone sync failed (non-critical):', e?.message);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await retryRequest(() => 
            apiClient.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          );
          // Only set user if email is verified
          if (response.data.email_verified) {
            setUser(response.data);
            // Auto-sync timezone (silently)
            const newTz = await syncTimezone(token, response.data.timezone);
            if (newTz) {
              setUser(prev => ({ ...prev, timezone: newTz }));
            }
          } else {
            // Clear token if email not verified
            localStorage.removeItem('vitala_token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth init error:', error);
          const status = error?.response?.status;
          // Only clear token for definitive auth failures (401, 403)
          // Do NOT clear on network errors or 5xx — token may still be valid
          if (status === 401 || status === 403) {
            localStorage.removeItem('vitala_token');
            setToken(null);
            setUser(null);
          }
          // For transient errors, keep the token and let user retry
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await retryRequest(() => 
      apiClient.post(`${API}/auth/login`, { email, password })
    );
    const { access_token, user: userData } = response.data;
    localStorage.setItem('vitala_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Auto-sync timezone on login
    const newTz = await syncTimezone(access_token, userData.timezone);
    if (newTz) setUser(prev => ({ ...prev, timezone: newTz }));
    return userData;
  };

  const biometricLogin = async (userId) => {
    const response = await retryRequest(() => 
      apiClient.post(`${API}/auth/biometric-login`, { user_id: userId })
    );
    const { access_token, user: userData } = response.data;
    localStorage.setItem('vitala_token', access_token);
    setToken(access_token);
    setUser(userData);
    // Auto-sync timezone on login
    const newTz = await syncTimezone(access_token, userData.timezone);
    if (newTz) setUser(prev => ({ ...prev, timezone: newTz }));
    return userData;
  };

  const register = async (name, email, password, consentData = null) => {
    // Register but DON'T auto-login - user must verify email first
    await retryRequest(() => 
      apiClient.post(`${API}/auth/register`, { 
        name, 
        email, 
        password,
        consent: consentData 
      })
    );
    // Don't set token or user - they need to verify email
    return { registered: true, email };
  };

  const logout = () => {
    localStorage.removeItem('vitala_token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, biometricLogin, register, logout, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
